import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Project } from 'src/app/models/project';
import { Role } from 'src/app/models/role';
import { App } from '../models';
import { FORM_CONSTANTS } from '../shared/constants';
import { ObjectProperties } from './logic-services';
import { FieldsLogicService } from './logic-services/fields-logic.service';
import { Column, Field, Section } from './logic-services/models';
import { RuleProperties } from './logic-services/role-rule-options';
import { SkhemaUtilityService } from './utility-services';

@Injectable({
    providedIn: 'root'
})
export class JsonFormsService {

    constructor(
        private _fieldsLogicService: FieldsLogicService,
        private _skhemaUtility: SkhemaUtilityService,
    ) { }

    getFieldRule(field: any): any {
        let rule;
        if (field.show) {
            if (field.enable) {
                rule = {
                    "rule": {
                        "effect": RuleProperties.Enable,
                        "condition": {
                            "scope": "#",
                            "schema": {}
                        }
                    }
                };
            }
            else {
                rule = {
                    "rule": {
                        "effect": RuleProperties.Enable,
                        "condition": {
                            "scope": "#",
                            "schema": { not: {} }
                        }
                    }
                };
            }
        }
        else {
            rule = {
                "rule": {
                    "effect": RuleProperties.Show,
                    "condition": {
                        "scope": "#",
                        "schema": { not: {} }
                    }
                }
            };
        }
        return rule;
    }

    parseFields(project: Project): any[] {
        let fields: any[] = [];

        function loopThroughSections(obj, _this): void {
            if (!obj) {
                return;
            }

            const keys = Object.keys(obj);
            keys.forEach(key => {
                if (key === ObjectProperties.FieldType) {
                    fields.push({
                        Name: obj[ObjectProperties.Name],
                        id: obj[ObjectProperties.Id],
                        FieldType: obj[ObjectProperties.FieldType],
                        projectId: obj[ObjectProperties.ProjectId]
                    });
                }

                if (
                    Array.isArray(obj[key]) ||
                    (typeof obj[key] === ObjectProperties.Object &&
                        obj[key] !== null)
                ) {
                    loopThroughSections(obj[key], _this);
                }
            });
        }

        if (project?.JSONForms) {
            loopThroughSections(project.JSONForms.Sections, this);
        }
        fields = fields.filter(field => {
            return !field.FieldType?.options?.readOnly ? true : false;
        });
        fields.sort((a, b) => (a.Name > b.Name) ? 1 : -1);
        return fields;
    }

    parseFieldRuleTable(project: Project, role: Role): Field[] {
        const ruleFields: Field[] = [];

        (project?.JSONForms?.Sections ?? []).forEach((section: Section) => {
            this.addSectionToRuleTable(section, role, ruleFields);
        });

        return ruleFields;
    }

    private addSectionToRuleTable(section: Section, role: Role, ruleFields: Field[]): void {
        ruleFields.push({
            Name: section.SectionTitle,
            type: ObjectProperties.Section,
            id: section.id,
            isGroupBy: true,
        });

        this.orderSectionContent(section).forEach((content: Column[] | Section) => {
            if (Array.isArray(content)) {
                content.forEach((column: Column) => {
                    this.addColumnToRuleTable(column, section.id, role, ruleFields);
                });
            }
            else {
                this.addSectionToRuleTable(content, role, ruleFields);
            }
        });
    }

    private addColumnToRuleTable(column: Column, sectionId: string, role: Role, ruleFields: Field[]): void {
        let parentGroup = sectionId;

        if (column.GroupTitle) {
            ruleFields.push({
                Name: column.GroupTitle,
                type: ObjectProperties.Group,
                id: column.id,
                isGroupBy: true,
                parentGroup: sectionId,
            });
            parentGroup = column.id;
        }

        (column.Fields ?? []).forEach((field: Field) => {
            const fieldRule = role?.rules?.[field.id]?.[RuleProperties.Rule];

            ruleFields.push({
                Name: field.Name,
                id: field.id,
                show: fieldRule ? (this.parseFieldRule(fieldRule, RuleProperties.Show) ?? true) : false,
                enable: fieldRule ? (this.parseFieldRule(fieldRule, RuleProperties.Enable) ?? false) : false,
                FieldType: field.FieldType,
                parentGroup,
            });
        });
    }

    private orderSectionContent(section: Section): (Column[] | Section)[] {
        const rows = new Map<string, Column[]>();
        (section.Columns ?? []).forEach((column: Column, columnIndex: number) => {
            const rowToken = `row:${column.row ?? Math.floor(columnIndex / FORM_CONSTANTS.DEFAULT_MAX_COLUMNS)}`;
            rows.set(rowToken, [...(rows.get(rowToken) ?? []), column]);
        });

        const subsections = new Map<string, Section>(
            (section.Sections ?? []).map((subsection: Section) => [`section:${subsection.id}`, subsection])
        );

        const sectionAndRowTokens = new Set<string>([...(section.contentOrder ?? []), ...rows.keys(), ...subsections.keys()]);

        return Array.from(sectionAndRowTokens)
            .map((token: string) => rows.get(token) ?? subsections.get(token))
            .filter((content: Column[] | Section | undefined): content is Column[] | Section => content !== undefined);
    }

    parseFieldRule(rule: any, type: string): boolean | null {
        const effect = rule['effect'];

        if (effect === type) {
            const status = rule[RuleProperties.Condition][RuleProperties.Schema];

            if (Object.keys(status).length === 0) {
                return true;
            }
            else if (Object.keys(status[RuleProperties.Not]).length === 0) {
                return false;
            }
            else {
                return true;
            }
        }

        return null;
    }

    setRules(currentApp: App, project: Project, role: Role): Observable<Project> {
        if (!project.JSONForms || project['rulesLoaded']) {
            return of(project);
        }

        const defaultRole = {
            "effect": RuleProperties.Show,
            "condition": {
                "scope": "#",
                "schema": { not: {} }
            }
        };

        return this._fieldsLogicService.getAllFieldsAndReadOnlyFields(currentApp, project).pipe(
            map(fieldsList => {
                // Create a deep copy to avoid mutating the original project
                const projectCopy = cloneDeep(project);

                for (const field of fieldsList) {
                    const rule = role.rules[field?.id] ? role.rules[field.id][RuleProperties.Rule] : defaultRole;
                    const isFieldHidden = rule.effect === RuleProperties.Show && rule.condition.schema.not !== undefined;

                    this.findFieldInSchemaAndApplyRule(
                        projectCopy?.JSONForms?.UISchema,
                        field.id,
                        rule,
                        isFieldHidden,
                        [],
                        Boolean(project.isSummaryProject)
                    );
                }
                projectCopy['rulesLoaded'] = true;
                return projectCopy;
            }),
            tap(projectCopy => {
                this._skhemaUtility.clearCache(projectCopy.id);
            })
        );
    }

    findFieldInSchemaAndApplyRule(object: any, fieldId: string, rule: any, isFieldHidden: boolean, parentIds: string[] = [], findAllMatches: boolean): string[] | undefined {
        const matchedParentIds: string[] = [];

        for (const key in object) {
            this.initalizeSectionAndAddToParents(object, parentIds);

            if (object[key] === fieldId) {
                object[RuleProperties.Rule] = rule;
                if (!findAllMatches) {
                    return parentIds;
                }

                parentIds.forEach((parentId: string) => {
                    if (!matchedParentIds.includes(parentId)) {
                        matchedParentIds.push(parentId);
                    }
                });
                continue;
            }

            if (!Array.isArray(object[key]) && typeof object[key] !== ObjectProperties.Object && object[key] !== null) {
                continue;
            }

            const parentIdsToUpdate = this.findFieldInSchemaAndApplyRule(object[key], fieldId, rule, isFieldHidden, [...parentIds], findAllMatches);

            if (parentIdsToUpdate === undefined || parentIdsToUpdate.length <= 0) {
                continue;
            }

            if (object.type === ObjectProperties.Group && object.emptySection && (parentIdsToUpdate.includes(object[ObjectProperties.Id]) || parentIdsToUpdate.includes(object[ObjectProperties.Label]))) {
                object.emptySection = isFieldHidden;
            }

            if (!findAllMatches) {
                return parentIdsToUpdate;
            }

            parentIdsToUpdate.forEach((parentId: string) => {
                if (!matchedParentIds.includes(parentId)) {
                    matchedParentIds.push(parentId);
                }
            });
        }

        return matchedParentIds.length ? matchedParentIds : undefined;
    }

    initalizeSectionAndAddToParents(object: any, parentIds: string[]): void {
        if (object.type === ObjectProperties.Group && object.emptySection !== false) {
            object[ObjectProperties.Id] ? parentIds.push(object[ObjectProperties.Id]) : parentIds.push(object[ObjectProperties.Label]);
            object.emptySection = true;
        }
    }
}
